import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '../ui/table';
import { BoulderAttemptMarkers } from './BoulderAttemptMarkers';
import { RefreshFlash } from './RefreshFlash';
import { boulderCardSignature } from './refresh-flash';
import { BoulderActionButtons } from './BoulderActionButtons';
import { RoundClimbThumbnail } from './RoundClimbThumbnail';
import { byBlockName } from './blockOrder';
import { cn } from '../../lib/utils';
import type { RoundClimb, BoulderRoundResult } from '../../types/competition-round';

interface ParticipantBoulderBlocksTableProps {
  roundClimbs: RoundClimb[];
  /** Whether the round still takes the athlete's marks: running, or in its corrections. */
  scoringOpen: boolean;
  onRecordAttempt: (roundClimb: RoundClimb) => void | Promise<void>;
  onRecordZone: (roundClimb: RoundClimb) => void | Promise<void>;
  onRecordTop: (roundClimb: RoundClimb) => void | Promise<void>;
  onEdit: (roundClimbId: string) => void;
}

const boulderResultOf = (roundClimb: RoundClimb) => roundClimb.my_result as BoulderRoundResult | null;
const isTopped = (roundClimb: RoundClimb) => boulderResultOf(roundClimb)?.topped === true;

/**
 * The athlete's own blocks in one round, table-shaped like the judge's own
 * scoresheet: tap a block to arm Attempt/Zone/Top for it, only one open at a
 * time. A topped block is finished business — it goes grey and sinks under a
 * "Completed" divider, out of the way of what is still being climbed — but it
 * can still be selected, for the pencil that takes the top back off.
 *
 * A block that needs a judge has nothing here to tap: it shows read-only,
 * whatever the judge has recorded so far.
 */
export function ParticipantBoulderBlocksTable({
  roundClimbs,
  scoringOpen,
  onRecordAttempt,
  onRecordZone,
  onRecordTop,
  onEdit,
}: ParticipantBoulderBlocksTableProps) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);

  const active = roundClimbs.filter(rc => !isTopped(rc)).sort(byBlockName);
  const completed = roundClimbs.filter(rc => isTopped(rc)).sort(byBlockName);

  const renderRow = (roundClimb: RoundClimb) => {
    const result = boulderResultOf(roundClimb);
    const attempts = result?.boulder_attempts || [];
    const topped = result?.topped || false;
    const canRecord = !roundClimb.requires_judge && scoringOpen;
    // The row tapped last, as on the judge's scoresheet. A topped block is
    // selected too, so its marks can be put straight — but it shows only the
    // pencil: there is nothing to add to a top.
    const selected = openId === roundClimb.id && canRecord;
    const open = selected && !topped;

    const select = () => {
      if (canRecord) setOpenId(roundClimb.id);
    };

    return (
      <Fragment key={roundClimb.id}>
        <TableRow
          className={cn('border-0', topped && 'text-muted-foreground', canRecord && 'cursor-pointer')}
          data-state={selected ? 'selected' : undefined}
          data-testid="block-row"
          onClick={select}
        >
          <TableCell className="py-2">
            <div className="flex items-center gap-3">
              <RoundClimbThumbnail roundClimb={roundClimb} className="h-12 w-12" />
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{roundClimb.display_name}</span>
                {roundClimb.climb?.difficulty && (
                  <Badge variant="outline" className="text-xs">{roundClimb.climb.difficulty}</Badge>
                )}
                {roundClimb.requires_judge && (
                  <Badge variant="outline" className="text-xs text-blue-600 border-blue-600">
                    {t('competitionRounds.requiresJudge')}
                  </Badge>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>

        <TableRow
          className={cn(open && 'border-0', canRecord && 'cursor-pointer')}
          data-state={selected ? 'selected' : undefined}
          data-testid="block-attempts-row"
          onClick={select}
        >
          <TableCell className="pt-0 pb-2">
            <div className="flex items-center gap-2">
              {/* Lit when the page's refresh brings a mark a judge put here. */}
              <RefreshFlash
                value={boulderCardSignature(attempts, topped, result?.zone || false)}
                className={cn('flex items-center gap-2', topped && 'opacity-60')}
              >
                <BoulderAttemptMarkers attempts={attempts} />
              </RefreshFlash>

              {/* Only on the selected row: on every row it was a column of
                  pencils to tap by mistake. */}
              {selected && attempts.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  data-testid="block-edit-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(roundClimb.id);
                  }}
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>

        {open && (
          <TableRow data-state="selected" data-testid="block-actions-row">
            <TableCell className="pt-0 pb-3">
              <BoulderActionButtons
                topped={topped}
                onRecordAttempt={() => onRecordAttempt(roundClimb)}
                onRecordZone={() => onRecordZone(roundClimb)}
                onRecordTop={() => onRecordTop(roundClimb)}
              />
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  };

  if (roundClimbs.length === 0) {
    return (
      <p className="text-center text-muted-foreground py-8">
        {t('competitionRounds.noClimbs')}
      </p>
    );
  }

  return (
    <Table>
      <TableBody>
        {active.map(renderRow)}

        {completed.length > 0 && (
          <TableRow className="hover:bg-transparent" data-testid="completed-divider">
            <TableCell className="pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('competitionRounds.completedBlocks', { defaultValue: 'Completed' })}
            </TableCell>
          </TableRow>
        )}

        {completed.map(renderRow)}
      </TableBody>
    </Table>
  );
}
